import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface UserAvatarProps {
  avatarUrl?: string | null;
  username?: string | null;
  isPremium?: boolean;
  premiumType?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-14 w-14",
  xl: "h-20 w-20",
};

const emojiSizeClasses = {
  sm: "text-lg",
  md: "text-xl",
  lg: "text-3xl",
  xl: "text-4xl",
};

const fallbackTextClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-lg",
  xl: "text-xl",
};

const borderClasses = {
  sm: "ring-[1.5px] ring-offset-1",
  md: "ring-2 ring-offset-1",
  lg: "ring-2 ring-offset-2",
  xl: "ring-[3px] ring-offset-2",
};

// Film reel sprocket sizes per avatar size
const sprocketConfig = {
  sm: { count: 12, size: 3, ring: 2 },
  md: { count: 14, size: 3.5, ring: 2 },
  lg: { count: 16, size: 4, ring: 3 },
  xl: { count: 20, size: 5, ring: 3 },
};

const UserAvatar = ({ avatarUrl, username, isPremium, premiumType, size = "md", className }: UserAvatarProps) => {
  const isEmoji = avatarUrl?.startsWith("emoji:");
  const emoji = isEmoji ? avatarUrl!.slice(6) : null;
  const initial = username?.charAt(0)?.toUpperCase() || "?";
  const isLifetime = premiumType === "lifetime";

  return (
    <div className={cn("relative", className)}>
      <Avatar
        className={cn(
          sizeClasses[size],
          isPremium && cn(
            borderClasses[size],
            "ring-offset-background",
            "ring-transparent",
          ),
        )}
        style={isPremium ? {
          boxShadow: `0 0 0 ${size === "xl" ? "3px" : "2px"} hsl(220 15% 8%), 0 0 0 ${size === "xl" ? "5px" : "4px"} transparent`,
          border: `2px solid transparent`,
          backgroundClip: "padding-box",
        } : undefined}
      >
        {emoji ? (
          <div className={cn("flex items-center justify-center w-full h-full bg-secondary", emojiSizeClasses[size])}>
            {emoji}
          </div>
        ) : avatarUrl && !isEmoji ? (
          <AvatarImage src={avatarUrl} alt={username || "User"} className="object-cover" />
        ) : null}
        <AvatarFallback className={cn("bg-primary/15 text-primary font-display font-bold", fallbackTextClasses[size])}>
          {initial}
        </AvatarFallback>
      </Avatar>
      
      {/* Premium flair */}
      {isPremium && (
        isLifetime ? (
          /* Lifetime: spinning film reel border */
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ animation: "film-reel-spin 8s linear infinite" }}
          >
            <svg viewBox="0 0 100 100" className="w-full h-full" style={{ overflow: "visible" }}>
              {/* Outer film strip ring */}
              <circle cx="50" cy="50" r="48" fill="none" stroke="hsl(45 93% 58%)" strokeWidth={sprocketConfig[size].ring} opacity="0.8" />
              {/* Sprocket holes */}
              {Array.from({ length: sprocketConfig[size].count }).map((_, i) => {
                const angle = (i / sprocketConfig[size].count) * 360;
                const rad = (angle * Math.PI) / 180;
                const x = 50 + 48 * Math.cos(rad);
                const y = 50 + 48 * Math.sin(rad);
                return (
                  <rect
                    key={i}
                    x={x - sprocketConfig[size].size / 2}
                    y={y - sprocketConfig[size].size / 2}
                    width={sprocketConfig[size].size}
                    height={sprocketConfig[size].size}
                    rx="0.8"
                    fill="hsl(220 15% 8%)"
                    stroke="hsl(45 93% 58%)"
                    strokeWidth="0.6"
                    transform={`rotate(${angle}, ${x}, ${y})`}
                  />
                );
              })}
            </svg>
          </div>
        ) : (
          /* Monthly: gradient ring */
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              padding: size === "xl" ? "3px" : "2px",
              background: "linear-gradient(135deg, hsl(231 99% 62%), hsl(280 90% 60%), hsl(330 80% 55%), hsl(45 93% 58%), hsl(231 99% 62%))",
              backgroundSize: "300% 300%",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
              animation: "premium-ring-rotate 4s linear infinite",
            }}
          />
        )
      )}
    </div>
  );
};

export default UserAvatar;
